import { type SupportProfileCommitGuard, runGuardedSupportProfileCommit } from './support-customer-ai';

interface SupportProfileRequestOptions<T> {
  force: boolean;
  commitGuard: SupportProfileCommitGuard;
  execute(requestCommitGuard: SupportProfileCommitGuard): Promise<T | null>;
  onStart(): void;
  onFinish(): void;
}

export interface SupportProfileRequestCoordinator<T> {
  run(options: SupportProfileRequestOptions<T>): Promise<T | null>;
}

export function createSupportProfileRequestCoordinator<T>(): SupportProfileRequestCoordinator<T> {
  let activeRequestId = 0;
  let activeRequest: Promise<T | null> | null = null;

  return {
    async run(options) {
      const { force, commitGuard, execute, onStart, onFinish } = options;

      if (!force && activeRequest) {
        const value = await activeRequest;
        return runGuardedSupportProfileCommit(commitGuard, () => {}) ? value : null;
      }

      let requestId = 0;
      if (
        !runGuardedSupportProfileCommit(commitGuard, () => {
          requestId = activeRequestId + 1;
          activeRequestId = requestId;
          onStart();
        })
      ) {
        return null;
      }

      const requestCommitGuard = () => requestId === activeRequestId && commitGuard();
      const request = (async () => {
        try {
          return await execute(requestCommitGuard);
        } finally {
          if (requestId === activeRequestId) onFinish();
        }
      })();

      activeRequest = request;
      try {
        return await request;
      } finally {
        if (activeRequest === request) activeRequest = null;
      }
    }
  };
}
